import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { FileText, Search as SearchIcon } from "lucide-react";
import { useSearch, useSpaces } from "@/api/hooks";
import type { SearchHit } from "@/api/types";
import { cn } from "@/lib/utils";
import { PageHeader, EmptyState, ErrorState } from "@/components/States";
import { Input } from "@/components/ui/input";
import { PageSkeleton } from "@/components/ui/skeleton";

function groupHits(hits: SearchHit[]) {
  const map = new Map<string, { noteId: string; title: string | null; score: number; matchedBy: SearchHit["matchedBy"]; hits: SearchHit[] }>();
  for (const h of hits) {
    const g = map.get(h.noteId) ?? { noteId: h.noteId, title: h.title, score: h.score, matchedBy: [], hits: [] };
    for (const m of h.matchedBy ?? []) if (!g.matchedBy.includes(m)) g.matchedBy.push(m);
    g.hits.push(h);
    g.score = Math.max(g.score, h.score);
    map.set(h.noteId, g);
  }
  return [...map.values()].sort((a, b) => b.score - a.score);
}

/** Highlight query terms in a plain-text snippet. */
export function Highlight({ text, query, className }: { text: string; query: string; className?: string }) {
  const terms = useMemo(
    () =>
      query
        .toLowerCase()
        .split(/\s+/)
        .map((t) => t.replace(/[^\p{L}\p{N}]/gu, ""))
        .filter((t) => t.length > 1),
    [query],
  );
  if (terms.length === 0) return <span className={className}>{text}</span>;
  const re = new RegExp(`(${terms.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")})`, "gi");
  const parts = text.split(re);
  return (
    <span className={className}>
      {parts.map((p, i) =>
        terms.includes(p.toLowerCase()) ? (
          <mark key={i} className="rounded-sm bg-tone-warning/30 px-0.5 text-foreground">
            {p}
          </mark>
        ) : (
          <span key={i}>{p}</span>
        ),
      )}
    </span>
  );
}

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") ?? "";
  const space = params.get("space") ?? "";
  const [input, setInput] = useState(q);
  const spaces = useSpaces();

  useEffect(() => setInput(q), [q]);
  useEffect(() => {
    const t = setTimeout(() => {
      if (input.trim() !== q) setParams(input.trim() ? { q: input.trim(), ...(space ? { space } : {}) } : {}, { replace: true });
    }, 250);
    return () => clearTimeout(t);
  }, [input]); // eslint-disable-line react-hooks/exhaustive-deps

  const results = useSearch(q, space || undefined);
  const groups = groupHits(results.data ?? []);

  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader title="Search" description="Full-text and semantic search across your notes." />
      <form
        className="mb-6 flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          setParams(input.trim() ? { q: input.trim(), ...(space ? { space } : {}) } : {});
        }}
      >
        <div className="relative flex-1">
          <SearchIcon className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={input} onChange={(e) => setInput(e.target.value)} placeholder="What are you looking for?" className="h-10 pl-9 text-base" autoFocus aria-label="Search query" />
        </div>
        {spaces.data && spaces.data.length > 1 && (
          <select
            value={space}
            onChange={(e) => setParams(q ? { q, ...(e.target.value ? { space: e.target.value } : {}) } : e.target.value ? { space: e.target.value } : {})}
            className="h-10 rounded-lg border bg-card px-2 text-sm"
            aria-label="Space"
          >
            <option value="">All spaces</option>
            {spaces.data.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        )}
      </form>

      {!q && <EmptyState icon={SearchIcon} title="Type to search" description="Results are grouped by note; click a hit to jump to its block." />}
      {q && results.isLoading && <PageSkeleton />}
      {q && results.isError && <ErrorState error={results.error} retry={() => results.refetch()} />}
      {q && results.data && groups.length === 0 && <EmptyState icon={SearchIcon} title={`No results for “${q}”`} />}
      {q && groups.length > 0 && (
        <div className={cn("flex flex-col gap-3", results.isFetching && "opacity-70")}>
          <div className="px-1 text-xs text-muted-foreground">
            {results.data!.length} {results.data!.length === 1 ? "hit" : "hits"} in {groups.length} {groups.length === 1 ? "note" : "notes"}
          </div>
          {groups.map((g) => (
            <article key={g.noteId} className="rounded-xl border bg-card">
              <Link to={`/notes/${g.noteId}`} className="flex items-center gap-2 border-b px-4 py-2.5 hover:bg-muted/40">
                <FileText className="size-4 text-muted-foreground" />
                <span className="truncate font-medium">{g.title || "Untitled"}</span>
                <span className="ml-auto flex shrink-0 gap-1">
                  {g.matchedBy.map((m) => (
                    <span key={m} className="rounded-full border px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground" title={m === "semantic" ? "Found by meaning (embeddings)" : "Found by the words themselves"}>
                      {m === "semantic" ? "meaning" : "text"}
                    </span>
                  ))}
                </span>
              </Link>
              <ul className="divide-y">
                {g.hits.map((h, i) => (
                  <li key={`${h.blockId ?? i}`}>
                    <Link to={`/notes/${h.noteId}${h.blockId ? `#${h.blockId}` : ""}`} className="flex items-start gap-3 px-4 py-2.5 text-sm hover:bg-muted/40">
                      <Highlight text={h.snippet} query={q} className="line-clamp-3 flex-1 leading-relaxed text-foreground/85" />
                      {h.blockId && <span className="shrink-0 font-mono text-[11px] text-muted-foreground">#{h.blockId}</span>}
                    </Link>
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
